import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { EntityRef } from '../../components/EntityRef'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { listLegalEntities } from '../../api/legalEntities'
import { ApiError } from '../../api/problem'
import { label } from '../../lib/labels'
import { rsqlEquals } from '../../lib/rsql'
import type { LegalEntityView } from '../../types/account'

/**
 * Named `*Stub` — this is the real drill-down target for the Accounts list's
 * "Legal entity" count chip (a live `GET /legal-entities?filter=accountId==…`
 * call, real rows), but it is deliberately not the full M23 ListView
 * treatment (no filter/sort/columns/saved-views/export here yet) — that is
 * the Legal Entities module's own story. Registered as a real route so the
 * chip is never a dead link.
 */
export function LegalEntitiesListStub() {
  const [searchParams] = useSearchParams()
  const accountId = searchParams.get('accountId')
  const [entities, setEntities] = useState<LegalEntityView[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    listLegalEntities({ filter: accountId ? rsqlEquals('accountId', accountId) : null, limit: 100 })
      .then((page) => {
        if (!cancelled) setEntities(page.items)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.problem.detail ?? err.message : 'Could not load legal entities.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [accountId])

  return (
    <div style={{ padding: 'var(--iw-space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)' }}>
        <span style={{ color: 'var(--iw-accent)' }}>
          <Icon name="landmark" size={20} />
        </span>
        <h1 style={{ margin: 0, fontSize: 'var(--iw-size-lg)' }}>Legal entities</h1>
        <span style={{ flex: 1 }} />
        {accountId ? (
          <Link className="iw-btn iw-btn--primary" to={`/clients/legal-entities/new?accountId=${accountId}`}>
            New legal entity
          </Link>
        ) : null}
      </div>

      {accountId ? (
        <p style={{ margin: 0, fontSize: 'var(--iw-size-sm)', color: 'var(--iw-text-muted)' }}>
          Filtered to{' '}
          <Link to={`/clients/accounts/${accountId}`} style={{ color: 'var(--iw-accent)' }}>
            this account
          </Link>
          .
        </p>
      ) : null}

      {error ? (
        <InlineAlert severity="error">{error}</InlineAlert>
      ) : loading ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>Loading legal entities…</p>
      ) : entities.length === 0 ? (
        <InlineAlert severity="info">
          No legal entities {accountId ? 'for this account' : 'yet'}.
          {accountId ? (
            <>
              {' '}
              <Link to={`/clients/legal-entities/new?accountId=${accountId}`} style={{ color: 'var(--iw-accent)' }}>
                Create one
              </Link>
              .
            </>
          ) : null}
        </InlineAlert>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--iw-size-sm)' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--iw-border)' }}>
              <th style={{ padding: 'var(--iw-space-2)' }}>Name</th>
              <th style={{ padding: 'var(--iw-space-2)' }}>Account</th>
              <th style={{ padding: 'var(--iw-space-2)' }}>Jurisdiction</th>
              <th style={{ padding: 'var(--iw-space-2)' }}>Tax IDs</th>
            </tr>
          </thead>
          <tbody>
            {entities.map((entity) => (
              <tr key={entity.id} style={{ borderBottom: '1px solid var(--iw-border)' }}>
                <td style={{ padding: 'var(--iw-space-2)' }}>
                  <Link to={`/clients/legal-entities/${entity.id}`} style={{ color: 'var(--iw-accent)', textDecoration: 'none' }}>
                    {entity.name}
                  </Link>
                </td>
                <td style={{ padding: 'var(--iw-space-2)' }}>
                  <EntityRef kind="account" id={entity.accountId} name={entity.accountName} href={`/clients/accounts/${entity.accountId}`} />
                </td>
                <td style={{ padding: 'var(--iw-space-2)' }}>{label('jurisdiction', entity.jurisdiction)}</td>
                <td style={{ padding: 'var(--iw-space-2)' }}>{entity.taxIdentifiers.length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
